// ====== services/labelService.js ======
import apiCall, { endpoints } from './api';

/**
 * Service pour la gestion des étiquettes et codes-barres
 */
export const labelService = {
  /**
   * Générer des étiquettes pour les produits sélectionnés
   */
  generateProductLabels: async (request) => {
    try {
      console.log('🏷️ Génération d\'étiquettes:', request);
      
      // Validation de la requête
      if (!request.productIds || request.productIds.length === 0) {
        return {
          success: false,
          message: 'Aucun produit sélectionné'
        };
      }

      if (!request.format || !['small', 'medium', 'large'].includes(request.format)) {
        return {
          success: false,
          message: 'Format d\'étiquette invalide'
        };
      }

      // Validation des copies
      if (request.copies) {
        for (const [productId, copyCount] of Object.entries(request.copies)) {
          if (copyCount < 1 || copyCount > 10) {
            return {
              success: false,
              message: `Nombre d'exemplaires invalide pour le produit ${productId}: ${copyCount}`
            };
          }
        }
      }

      const response = await apiCall.post(
        endpoints.manager.products.generateLabels, 
        request
      );
      
      if (response.success) {
        console.log('✅ Étiquettes générées:', response.data);
        return response;
      } else {
        console.error('❌ Erreur génération étiquettes:', response.message);
        return response;
      }

    } catch (error) {
      console.error('❌ Erreur lors de la génération d\'étiquettes:', error);
      return {
        success: false,
        message: 'Erreur lors de la génération des étiquettes'
      };
    }
  },

  /**
   * Valider des codes-barres
   */
  validateBarcodes: async (barcodes) => {
    try {
      console.log('🔍 Validation de codes-barres:', barcodes);
      
      if (!barcodes || barcodes.length === 0) {
        return {
          success: false,
          message: 'Aucun code-barres à valider'
        };
      }

      const response = await apiCall.post(
        endpoints.manager.products.validateBarcodes,
        { barcodes }
      );
      
      if (response.success) {
        console.log('✅ Validation terminée:', response.data);
        return response;
      } else {
        console.error('❌ Erreur validation codes-barres:', response.message);
        return response;
      }

    } catch (error) {
      console.error('❌ Erreur lors de la validation des codes-barres:', error);
      return {
        success: false,
        message: 'Erreur lors de la validation des codes-barres'
      };
    }
  },

  /**
   * Scanner un produit par code-barres
   */
  scanProductByBarcode: async (barcode) => {
    try {
      console.log('📱 Scan du code-barres:', barcode);
      
      if (!barcode || barcode.trim() === '') {
        return {
          success: false,
          message: 'Code-barres requis'
        };
      }

      const response = await apiCall.get(
        endpoints.manager.products.scanByBarcode(barcode.trim())
      );
      
      if (response.success) {
        console.log('✅ Produit trouvé:', response.data);
        return response;
      } else {
        console.error('❌ Produit non trouvé:', response.message);
        return response;
      }

    } catch (error) {
      console.error('❌ Erreur lors du scan:', error);
      return {
        success: false,
        message: 'Produit non trouvé avec ce code-barres'
      };
    }
  },

  /**
   * Obtenir les statistiques des codes-barres
   */
  getBarcodeStats: async () => {
    try {
      console.log('📊 Récupération des statistiques codes-barres');
      
      const response = await apiCall.get(endpoints.manager.products.barcodeStats);
      
      if (response.success) {
        console.log('✅ Statistiques récupérées:', response.data);
        return response;
      } else {
        console.error('❌ Erreur récupération stats:', response.message);
        return response;
      }

    } catch (error) {
      console.error('❌ Erreur lors de la récupération des statistiques:', error);
      return {
        success: false,
        message: 'Erreur lors de la récupération des statistiques'
      };
    }
  },

  /**
   * Obtenir les produits sans codes-barres
   */
  getProductsWithoutBarcodes: async () => {
    try {
      console.log('🔍 Récupération des produits sans codes-barres');
      
      const response = await apiCall.get(endpoints.manager.products.withoutBarcodes);
      
      if (response.success) {
        console.log('✅ Produits sans codes-barres récupérés:', response.data);
        return response;
      } else {
        console.error('❌ Erreur récupération produits:', response.message);
        return response;
      }

    } catch (error) {
      console.error('❌ Erreur lors de la récupération des produits:', error);
      return {
        success: false,
        message: 'Erreur lors de la récupération des produits'
      };
    }
  }
};

// Utilitaires pour les étiquettes
export const labelUtils = {
  /**
   * Formats d'étiquettes disponibles
   */
  formats: {
    small: { code: 'small', name: 'Petit', width: 160, height: 100 },
    medium: { code: 'medium', name: 'Moyen', width: 200, height: 120 },
    large: { code: 'large', name: 'Grand', width: 240, height: 140 }
  },

  /**
   * Obtenir les dimensions d'un format
   */
  getFormatDimensions: (format) => {
    return labelUtils.formats[format] || labelUtils.formats.medium;
  },

  /**
   * Formater le prix pour l'affichage
   */
  formatPrice: (price) => {
    if (!price) return '0 FCFA';
    return new Intl.NumberFormat('fr-FR', {
      style: 'decimal',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price) + ' FCFA';
  },

  /**
   * Raccourcir le nom du produit pour l'étiquette
   */
  truncateProductName: (name, maxLength = 25) => {
    if (!name) return '';
    return name.length > maxLength ? name.substring(0, maxLength) + '...' : name;
  },

  /**
   * Valider un code-barres (format basique)
   */
  isValidBarcode: (barcode) => {
    if (!barcode) return false;
    // Vérification basique: au moins 8 caractères, alphanumériques
    return /^[0-9A-Za-z]{8,}$/.test(barcode.trim());
  },

  /**
   * Calculer le total d'étiquettes à partir des copies
   */
  calculateTotalLabels: (copies) => {
    if (!copies) return 0;
    return Object.values(copies).reduce((total, count) => total + (count || 0), 0);
  },

  /**
   * Créer la configuration d'impression par défaut
   */
  getDefaultPrintConfig: () => ({
    format: 'medium',
    showPrice: true,
    showSku: true,
    showLogo: true,
    margin: 4
  })
};

export default labelService;